import { memo, useMemo } from "react";
import { Organization_Base } from "~/common/typescript/models/Organization";
import { AC_Organization, AC_Organization_Role } from "~/common/typescript/services/access_control/AC_Organization";
import styled from "styled-components";
import { TeamOutlined, CrownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { RoutesPrefix } from "~/routes/routes";
import UserAvatar from "~/layouts/components/Header/components/UserAvatar/UserAvatar";
import { User_Base } from "~/common/typescript/models/User";
import { useAtom } from "jotai";
import { userACRoles } from "~/stores/UserStore";
import { AC_TLGeo_Role } from "~/common/typescript/services/access_control/AC_Tlgeo";
import defaultBg from './default_organization_image.jpg';

export interface OrganizationItemProps {
  organization: Organization_Base;
  role?: AC_Organization_Role;
}

const OrganizationItem = (props: OrganizationItemProps) => {
  const navigate = useNavigate();
  const [roles] = useAtom(userACRoles);
  const isAdmin = roles.includes(AC_TLGeo_Role.TLGEO_ADMIN);

  const onClickDetailOrg = () => {
    navigate(`${RoutesPrefix.ORGANIZATION}/${props.organization.id}`);
  };

  const owner = typeof props.organization?.owner === 'object' ? props.organization.owner : undefined;

  const ownerDisplayName = useMemo(() => {
    if (!owner) return 'Chưa cập nhật';
    const fullname = owner.fullname?.trim();
    const username = owner.username?.trim();

    if (isAdmin) {
      if (fullname && username && fullname !== username) {
        return `${fullname} (${username})`;
      }
      return username || fullname || 'Chưa cập nhật';
    }

    return fullname || username || 'Chưa cập nhật';
  }, [owner, isAdmin]);

  const memberCount = useMemo(() => {
    if (typeof props.organization?.memberCount === 'number') {
      return props.organization.memberCount;
    }
    return 1;
  }, [props.organization?.memberCount]);

  const maxMembers = props.organization?.maxNMembers;
  const memberCountText = maxMembers ? `${memberCount}/${maxMembers} thành viên` : `${memberCount} thành viên`;

  const isSharedRole = props.role && props.role !== AC_Organization_Role.ORGANIZATION_OWNER;

  return (
    <UI_Wrapper key={props.organization.id} onClick={onClickDetailOrg}>
      <UI_BGImage src={defaultBg} alt="" />

      {isSharedRole && (
        <UI_SharedBadge>{AC_Organization.getRoleName(props.role!)}</UI_SharedBadge>
      )}

      <UI_Overlay>
        <UI_Name title={props.organization.name}>{props.organization.name}</UI_Name>
        {props.organization.description && (
          <UI_Description title={props.organization.description}>
            {props.organization.description}
          </UI_Description>
        )}

        <UI_MetaRow>
          <UI_OwnerInfo title={`Chủ sở hữu: ${ownerDisplayName}`}>
            {owner ? (
              <UserAvatar user={owner} size={20} />
            ) : (
              <CrownOutlined style={{ color: '#fbbf24', fontSize: 13, flexShrink: 0 }} />
            )}
            <UI_OwnerName>{ownerDisplayName}</UI_OwnerName>
          </UI_OwnerInfo>

          <UI_MemberTag title={`Số thành viên: ${memberCountText}`}>
            <TeamOutlined style={{ fontSize: 13 }} />
            <span>{memberCountText}</span>
          </UI_MemberTag>
        </UI_MetaRow>
      </UI_Overlay>
    </UI_Wrapper>
  );
};

export default memo(OrganizationItem);

const UI_Wrapper = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
`;

const UI_BGImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UI_SharedBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  background: rgba(37, 99, 235, 0.9);
  backdrop-filter: blur(4px);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const UI_Overlay = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  padding: 10px 12px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0) 0%,
    rgba(15, 23, 42, 0.6) 25%,
    rgba(15, 23, 42, 0.92) 100%
  );
  display: flex;
  flex-direction: column;
  gap: 5px;
  box-sizing: border-box;
  z-index: 1;
`;

const UI_Name = styled.h3`
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;

const UI_Description = styled.p`
  color: #cbd5e1;
  font-size: 12px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  line-height: 1.3;
`;

const UI_MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
  min-width: 0;
`;

const UI_OwnerInfo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

const UI_OwnerName = styled.span`
  color: #f1f5f9;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;

const UI_MemberTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  color: #e2e8f0;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 4px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
`;

